import React, { useState, useEffect, useCallback } from "react";
import {
  Card, Table, Badge, Form, Button, Row, Col, Alert, Spinner,
} from "react-bootstrap";
import api from "../services/api";
import { useLanguage } from "../i18n/LanguageContext";

interface LogEntry {
  id: number;
  timestamp: string;
  level: "info" | "warn" | "alert";
  event: string;
  user_id?: number;
  user_name?: string;
  ip?: string;
  details?: string;
}

interface Summary {
  total: number;
  alerts: number;
  warnings: number;
  failedLogins24h: number;
  suspiciousActivity24h: number;
}

const LEVEL_VARIANT: Record<string, string> = {
  info: "primary",
  warn: "warning",
  alert: "danger",
};

const EVENT_LABELS: Record<string, string> = {
  login_success: "logsEventLoginSuccess",
  login_failed: "logsEventLoginFailed",
  login_2fa_failed: "logsEvent2faFailed",
  login_2fa_success: "logsEvent2faSuccess",
  unauthorized_access: "logsEventUnauthorized",
  suspicious_activity: "logsEventSuspicious",
  password_changed: "logsEventPasswordChanged",
  admin_action: "logsEventAdminAction",
  batch_upload: "logsEventBatchUpload",
  bulk_delete: "logsEventBulkDelete",
};

const PAGE_SIZE = 50;

const AdminLogs: React.FC = () => {
  const { t, dir } = useLanguage();
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [filterLevel, setFilterLevel] = useState("all");
  const [filterEvent, setFilterEvent] = useState("all");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const loadSummary = useCallback(async () => {
    try {
      const r = await api.get("/audit-log/summary");
      setSummary(r.data);
    } catch {}
  }, []);

  const loadLogs = useCallback(async (reset = false) => {
    setLoading(true);
    setError("");
    const currentOffset = reset ? 0 : offset;
    try {
      const params: any = { limit: PAGE_SIZE, offset: currentOffset };
      if (filterLevel !== "all") params.level = filterLevel;
      if (filterEvent !== "all") params.event = filterEvent;
      if (search) params.search = search;

      const r = await api.get("/audit-log", { params });
      const newEntries: LogEntry[] = r.data.entries;

      if (reset) {
        setEntries(newEntries);
        setOffset(PAGE_SIZE);
      } else {
        setEntries((prev) => [...prev, ...newEntries]);
        setOffset(currentOffset + PAGE_SIZE);
      }
      setTotal(r.data.total);
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to load logs");
    } finally {
      setLoading(false);
    }
  }, [offset, filterLevel, filterEvent, search]);

  useEffect(() => {
    loadSummary();
    loadLogs(true);
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => { loadSummary(); loadLogs(true); }, 30000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload when filters change
  useEffect(() => {
    setOffset(0);
    loadLogs(true);
  }, [filterLevel, filterEvent, search]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  const formatTime = (ts: string) => {
    const d = new Date(ts);
    return d.toLocaleString();
  };

  const eventLabel = (event: string) => {
    const key = EVENT_LABELS[event];
    return key ? t(key as any) : event.replace(/_/g, " ");
  };

  return (
    <div dir={dir}>
      {/* Summary cards */}
      {summary && (
        <Row className="g-2 mb-3">
          {[
            { label: t("logsSummaryAlerts"), value: summary.alerts, variant: "danger", icon: "🚨" },
            { label: t("logsSummaryFailed24h"), value: summary.failedLogins24h, variant: "warning", icon: "🔐" },
            { label: t("logsSummarySuspicious"), value: summary.suspiciousActivity24h, variant: "danger", icon: "⚠️" },
            { label: t("logsSummaryWarnings"), value: summary.warnings, variant: "warning", icon: "🟡" },
            { label: t("logsSummaryTotal"), value: summary.total, variant: "secondary", icon: "📋" },
          ].map((stat) => (
            <Col xs={6} md={4} lg key={stat.label}>
              <Card className={`border-${stat.variant} h-100`}>
                <Card.Body className="p-2 text-center">
                  <div style={{ fontSize: 22 }}>{stat.icon}</div>
                  <div
                    className={`fw-bold text-${stat.variant}`}
                    style={{ fontSize: 22 }}
                  >
                    {stat.value}
                  </div>
                  <div className="text-muted" style={{ fontSize: 11 }}>
                    {stat.label}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Filters */}
      <Card className="mb-3 shadow-sm">
        <Card.Body className="p-2 p-md-3">
          <Row className="g-2 align-items-end">
            <Col xs={12} md={3}>
              <Form onSubmit={handleSearch}>
                <div className="d-flex gap-1">
                  <Form.Control
                    size="sm"
                    placeholder={t("logsSearch")}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                  />
                  <Button size="sm" type="submit" variant="outline-primary">🔍</Button>
                  {search && (
                    <Button size="sm" variant="outline-secondary" onClick={() => { setSearch(""); setSearchInput(""); }}>✕</Button>
                  )}
                </div>
              </Form>
            </Col>
            <Col xs={6} md={2}>
              <Form.Select size="sm" value={filterLevel} onChange={(e) => setFilterLevel(e.target.value)}>
                <option value="all">{t("logsLevelAll")}</option>
                <option value="info">{t("logsLevelInfo")}</option>
                <option value="warn">{t("logsLevelWarn")}</option>
                <option value="alert">{t("logsLevelAlert")}</option>
              </Form.Select>
            </Col>
            <Col xs={6} md={3}>
              <Form.Select size="sm" value={filterEvent} onChange={(e) => setFilterEvent(e.target.value)}>
                <option value="all">{t("logsEventAll")}</option>
                {Object.keys(EVENT_LABELS).map((ev) => (
                  <option key={ev} value={ev}>{eventLabel(ev)}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md="auto" className="ms-md-auto">
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => { loadSummary(); loadLogs(true); }}
                disabled={loading}
              >
                {loading ? <Spinner animation="border" size="sm" /> : `🔄 ${t("logsRefresh")}`}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

      {/* Log table */}
      <Card className="shadow-sm">
        <Card.Body className="p-0">
          <div className="d-none d-lg-block">
            <Table hover className="mb-0" style={{ fontSize: 13 }}>
              <thead className="table-dark">
                <tr>
                  <th style={{ width: 150 }}>{t("logsColTime")}</th>
                  <th style={{ width: 80 }}>{t("logsColLevel")}</th>
                  <th style={{ width: 150 }}>{t("logsColEvent")}</th>
                  <th style={{ width: 140 }}>{t("logsColUser")}</th>
                  <th style={{ width: 120 }}>{t("logsColIp")}</th>
                  <th>{t("logsColDetails")}</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && !loading ? (
                  <tr><td colSpan={6} className="text-center text-muted py-4">{t("logsNoEntries")}</td></tr>
                ) : (
                  entries.map((entry) => (
                    <tr
                      key={entry.id}
                      className={entry.level === "alert" ? "table-danger" : entry.level === "warn" ? "table-warning" : ""}
                    >
                      <td className="text-muted small" style={{ whiteSpace: "nowrap" }}>{formatTime(entry.timestamp)}</td>
                      <td>
                        <Badge bg={LEVEL_VARIANT[entry.level]} style={{ fontSize: 10 }}>
                          {entry.level.toUpperCase()}
                        </Badge>
                      </td>
                      <td><small>{eventLabel(entry.event)}</small></td>
                      <td>
                        <small>
                          {entry.user_name && <span className="fw-semibold">{entry.user_name}</span>}
                          {entry.user_id && <span className="text-muted ms-1">(#{entry.user_id})</span>}
                        </small>
                      </td>
                      <td><small className="text-muted font-monospace">{entry.ip || "—"}</small></td>
                      <td><small className="text-muted">{entry.details || "—"}</small></td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>

          {/* Mobile view */}
          <div className="d-lg-none">
            {entries.length === 0 && !loading ? (
              <div className="text-center text-muted py-4">{t("logsNoEntries")}</div>
            ) : (
              entries.map((entry) => (
                <div
                  key={entry.id}
                  className={`p-2 border-bottom ${entry.level === "alert" ? "bg-danger bg-opacity-10" : entry.level === "warn" ? "bg-warning bg-opacity-10" : ""}`}
                >
                  <div className="d-flex justify-content-between align-items-start mb-1">
                    <Badge bg={LEVEL_VARIANT[entry.level]} style={{ fontSize: 10 }}>
                      {entry.level.toUpperCase()}
                    </Badge>
                    <small className="text-muted">{formatTime(entry.timestamp)}</small>
                  </div>
                  <div className="fw-semibold small">{eventLabel(entry.event)}</div>
                  {entry.user_name && <div className="small text-muted">{entry.user_name} #{entry.user_id}</div>}
                  {entry.ip && <div className="small text-muted font-monospace">{entry.ip}</div>}
                  {entry.details && <div className="small text-muted">{entry.details}</div>}
                </div>
              ))
            )}
          </div>

          {/* Load more */}
          {entries.length < total && (
            <div className="text-center py-2 border-top">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => loadLogs(false)}
                disabled={loading}
              >
                {loading ? <Spinner animation="border" size="sm" /> : `${t("logsLoadMore")} (${total - entries.length} remaining)`}
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default AdminLogs;
